import * as React from 'react'
import Layout from '../components/layout'
import Seo from '../components/seo'
import { graphql, Link } from 'gatsby'
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Avatar,
  Divider,
  Stack,
  Breadcrumbs,
  Paper
} from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PersonIcon from '@mui/icons-material/Person'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ShareIcon from '@mui/icons-material/Share'
import { usePostHog } from '../hooks/usePostHog'
import { ArticleSchema, BreadcrumbListSchema } from '../components/schema'
import { agentBuilderSignupUrl, CTA_LABELS } from '../utils/ctaUrls'
import { captureMarketingCtaClick, CTA_SURFACES } from '../utils/marketingAnalytics'
import "../styles/wp-fallbacks.css"

const BlogPostTemplate = ({ data }) => {
  const { capture } = usePostHog()
  const post = data.wpPost

  React.useEffect(() => {
    capture('blog_post_viewed', { 
      post_title: post.title,
      post_uri: post.uri 
    })
  }, [post.title, post.uri, capture])

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href,
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
    }
    capture('blog_post_shared', { post_title: post.title })
  }

  return (
    <Layout pageTitle={post.title}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 4 }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            Home
          </Link>
          <Link to="/blog" style={{ textDecoration: 'none', color: 'inherit' }}>
            Blog
          </Link>
          <Typography color="text.primary">{post.title}</Typography>
        </Breadcrumbs>

        {/* Back Button */}
        <Button
          component={Link}
          to="/blog"
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          sx={{ mb: 4 }}
        >
          Back to Blog
        </Button>

        {/* Article Header */}
        <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            {post.categories?.nodes?.map((category) => (
              <Chip 
                key={category.slug}
                label={category.name} 
                size="small" 
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>
          
          <Typography 
            variant="h2" 
            sx={{ 
              mb: 3,
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 800,
              lineHeight: 1.2
            }}
          >
            {post.title}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Author and Date Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                src={post.author?.node?.avatar?.url}
                sx={{ width: 48, height: 48 }}
              >
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {post.author?.node?.name || 'Workload Team'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {post.date}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Button
              startIcon={<ShareIcon />}
              variant="outlined"
              onClick={handleShare}
              size="small"
            >
              Share
            </Button>
          </Box>
        </Paper>

        {/* Article Content */}
        <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Box
            sx={{
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 4,
                mb: 2,
                fontWeight: 700,
                color: 'text.primary'
              },
              '& h1': {
                fontSize: '2rem',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                pb: 1
              },
              '& h2': {
                fontSize: '1.75rem'
              },
              '& h3': {
                fontSize: '1.5rem'
              },
              '& p': {
                mb: 2,
                lineHeight: 1.7,
                fontSize: '1.1rem'
              },
              '& ul, & ol': {
                mb: 2,
                pl: 3
              },
              '& li': {
                mb: 1,
                lineHeight: 1.6
              },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 3,
                py: 2,
                my: 3,
                backgroundColor: 'grey.50',
                fontStyle: 'italic',
                borderRadius: '0 8px 8px 0'
              },
              '& code': {
                backgroundColor: 'grey.100',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.9em'
              },
              '& pre': {
                backgroundColor: 'grey.900',
                color: 'white',
                p: 3,
                borderRadius: 2,
                overflow: 'auto',
                mb: 3
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 2,
                my: 3
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }
            }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </Paper>

        {/* Tags */}
        {post.tags?.nodes?.length > 0 && (
          <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Tags
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {post.tags.nodes.map((tag) => (
                <Chip 
                  key={tag.slug}
                  component={Link}
                  clickable
                  label={tag.name} 
                  size="small" 
                  variant="outlined"
                  to={`/tag/${tag.slug}/`}
                />
              ))}
            </Stack>
          </Paper>
        )}

        {/* Related Posts or CTA */}
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Ready to automate the work that still runs by hand?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Describe the outcome. Workload builds and runs it across the apps you already use.
          </Typography>
          <Button
            component="a"
            href={agentBuilderSignupUrl}
            variant="contained"
            size="large"
            onClick={() =>
              captureMarketingCtaClick({
                cta_label: CTA_LABELS.primary,
                surface: CTA_SURFACES.blogPost,
                page_type: 'blog_post',
                destination: agentBuilderSignupUrl,
              })
            }
          >
            {CTA_LABELS.primary}
          </Button>
        </Paper>
      </Container>
    </Layout>
  )
}

export const query = graphql`
  query BlogPostQuery($id: String!) {
    wpPost(id: { eq: $id }) {
     seo {
        title
        metaDesc
        metaRobotsNoindex
        metaRobotsNofollow
        opengraphTitle
        opengraphDescription
        opengraphType
        opengraphImage { sourceUrl mediaItemUrl }
        twitterTitle
        twitterDescription
        twitterImage { sourceUrl mediaItemUrl }
        canonical
        breadcrumbs { text url }
        schema { raw }
      }
      id
      title
      content
      excerpt
      date(formatString: "MMMM D, YYYY")
      uri
      author {
        node {
          name
          avatar {
            url
          }
        }
      }
      featuredImage {
        node {
          sourceUrl
          altText
        }
      }
      categories {
        nodes {
          name
          slug
        }
      }
      tags {
        nodes {
          name
          slug
        }
      }
    }
  }
`

export const Head = ({ data }) => {
  const post = data.wpPost
  const siteUrl = 'https://www.workload.co'
  const postUrl = `${siteUrl}${post.uri}`
  const excerpt = post.excerpt.replace(/<[^>]*>/g, '')
  const featuredImage = post.featuredImage?.node?.sourceUrl || post.seo?.opengraphImage?.mediaItemUrl
  
  const breadcrumbItems = [
    { name: 'Home', url: siteUrl },
    { name: 'Blog', url: `${siteUrl}/blog` },
    { name: post.title, url: postUrl }
  ]

  return (
    <>
      <Seo 
        title={`${post.title} - Workload Blog`}
        description={excerpt}
        yoast={post.seo}
        pathname={post.uri}
        image={featuredImage}
        isArticle={true}
      />
      <ArticleSchema
        title={post.title}
        description={excerpt}
        url={postUrl}
        image={featuredImage}
        datePublished={post.date}
        dateModified={post.modified || post.date}
        author={{
          "@type": "Person",
          "name": post.author?.node?.name || "Workload Team"
        }}
      />
      <BreadcrumbListSchema items={breadcrumbItems} />
    </>
  )
}

export default BlogPostTemplate
